/**
 * Notification logic for tasks that transition into "needs me" — the
 * trigger half of the supervision loop. Pairs with `needsMe.ts` (the
 * destination half) to close `HUMAN-REVIEW-NOTIFICATIONS-1`.
 *
 * V1 ships in-app push only:
 *   - Browser Notifications API for system-level alerts (gated on user
 *     permission grant + tab visibility — no notification when the tab
 *     is foregrounded, since the user can already see the badge).
 *   - In-app bell with unseen-count pip for visual fallback.
 *
 * Backend channel adapters (Telegram, Slack, email) are explicitly
 * deferred — they're peers of the canonical in-app surface, not
 * replacements. See dispatch §"Recommendation — UPDATED 2026-05-15".
 *
 * The pure-logic functions below are testable without React or the
 * browser Notification API. The hook in `useNeedsMeNotifications` wraps
 * them and handles persistence + side effects.
 */

import type { TaskItem } from '@/api/types';
import { isNeedsMe, type NeedsMeContext } from '@/lib/needsMe';

const STORAGE_KEY_NOTIFIED = 'sp.board.needsme.notified.v1';
const STORAGE_KEY_ACKNOWLEDGED = 'sp.board.needsme.acknowledged.v1';
const STORAGE_KEY_BOOTSTRAPPED = 'sp.board.needsme.bootstrapped.v1';

/** Compute the set of task IDs currently in "needs me" state. */
export function computeNeedsMeIds(
  tasks: ReadonlyArray<TaskItem>,
  ctx: NeedsMeContext,
): Set<string> {
  const out = new Set<string>();
  for (const t of tasks) {
    if (isNeedsMe(t, ctx)) out.add(t.id);
  }
  return out;
}

/**
 * Tasks that just became "needs me" since the last notification cycle.
 *
 * A task is "new" if it's currently needs-me AND we haven't already fired
 * a notification for it. Resolved-then-resurfaced tasks WILL re-notify
 * (because they're removed from `notifiedIds` when they leave the set).
 */
export function newSinceLastSeen(
  currentNeedsMeIds: ReadonlySet<string>,
  notifiedIds: ReadonlySet<string>,
): string[] {
  const out: string[] = [];
  for (const id of currentNeedsMeIds) {
    if (!notifiedIds.has(id)) out.push(id);
  }
  return out;
}

/**
 * Updated `notifiedIds` after a notification cycle: keep only the IDs
 * still in the current set, then add the just-notified IDs. This causes
 * tasks that leave the needs-me set to be eligible for re-notification
 * if they re-enter later (e.g. the human rules, then someone reverts).
 */
export function updateNotifiedIds(
  currentNeedsMeIds: ReadonlySet<string>,
  notifiedIds: ReadonlySet<string>,
  justNotifiedIds: ReadonlyArray<string>,
): Set<string> {
  const out = new Set<string>();
  for (const id of notifiedIds) {
    if (currentNeedsMeIds.has(id)) out.add(id);
  }
  for (const id of justNotifiedIds) {
    out.add(id);
  }
  return out;
}

/** Tasks the user hasn't acknowledged yet — drives the bell pip. */
export function unseenCount(
  currentNeedsMeIds: ReadonlySet<string>,
  acknowledgedIds: ReadonlySet<string>,
): number {
  let n = 0;
  for (const id of currentNeedsMeIds) {
    if (!acknowledgedIds.has(id)) n++;
  }
  return n;
}

/**
 * Updated `acknowledgedIds` after the user opens the needs-me view: every
 * currently-needs-me task counts as acknowledged. IDs no longer in the
 * current set are dropped (so a task that re-enters needs-me later will
 * be unseen again, which is the right behavior — it's a new event).
 */
export function acknowledgeAll(
  currentNeedsMeIds: ReadonlySet<string>,
): Set<string> {
  return new Set(currentNeedsMeIds);
}

/**
 * Format a notification body from a TaskItem. Title + tags + link only —
 * NEVER include description, comments, or evidence URIs (they may carry
 * secrets per CLAUDE.md Principle 6c).
 */
export function formatNotificationBody(task: TaskItem): {
  title: string;
  body: string;
} {
  const tags = task.tags ?? [];
  const reason = inferReason(tags);
  const idChip = task.task_id ? `[${task.task_id}] ` : '';
  return {
    title: `Needs your input: ${idChip}${task.title}`,
    body: reason ? `Tagged ${reason}` : 'Tagged for human review',
  };
}

/**
 * Infer the human-readable reason a task is parked for review from its
 * meaning-gate / workflow tags.
 */
function inferReason(tags: ReadonlyArray<string>): string | null {
  const reasonOrder = [
    'decision-required',
    'legal-review',
    'security-review',
    'money-review',
    'partner-review',
    'product-review',
  ];
  for (const r of reasonOrder) {
    if (tags.includes(r)) return r;
  }
  if (tags.includes('workflow:human-review')) return 'workflow:human-review';
  return null;
}

// ── localStorage adapters (browser-side only) ────────────────────────────

function readSet(key: string): Set<string> {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed.filter((v): v is string => typeof v === 'string'));
  } catch {
    return new Set();
  }
}

function writeSet(key: string, set: ReadonlySet<string>): void {
  try {
    localStorage.setItem(key, JSON.stringify([...set]));
  } catch {
    // Quota exceeded or storage disabled — silent failure is OK; the
    // worst case is we re-notify on next session.
  }
}

export const notificationsStorage = {
  loadNotified: () => readSet(STORAGE_KEY_NOTIFIED),
  saveNotified: (set: ReadonlySet<string>) => writeSet(STORAGE_KEY_NOTIFIED, set),
  loadAcknowledged: () => readSet(STORAGE_KEY_ACKNOWLEDGED),
  saveAcknowledged: (set: ReadonlySet<string>) => writeSet(STORAGE_KEY_ACKNOWLEDGED, set),
  /**
   * "Bootstrapped" flag — true after the first computation. Used to
   * suppress notifications for tasks that were already needs-me before
   * the user even loaded the page. Without this, every page load would
   * fire a barrage of notifications for the existing backlog.
   */
  isBootstrapped: () => localStorage.getItem(STORAGE_KEY_BOOTSTRAPPED) === '1',
  markBootstrapped: () => {
    try { localStorage.setItem(STORAGE_KEY_BOOTSTRAPPED, '1'); } catch { /* noop */ }
  },
};
